import { useState } from 'react';
import { Badge, NavDropdown, Offcanvas } from 'react-bootstrap';
import { FaBell, FaCheckDouble } from 'react-icons/fa';

import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const formatRelativeDate = (value) => {
    if (!value) {
        return 'Ahora';
    }

    const timestamp = new Date(value).getTime();
    if (Number.isNaN(timestamp)) {
        return 'Ahora';
    }

    const seconds = Math.max(Math.floor((Date.now() - timestamp) / 1000), 0);

    if (seconds < 60) {
        return 'Hace un momento';
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        return `Hace ${minutes} min`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `Hace ${hours} h`;
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
        return `Hace ${days} d`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
        return `Hace ${months} mes${months > 1 ? 'es' : ''}`;
    }

    const years = Math.floor(months / 12);
    return `Hace ${years} an${years > 1 ? 'os' : 'o'}`;
};

const NotificationBellIcon = ({ unreadCount }) => (
    <span className="position-relative d-inline-flex align-items-center">
        <FaBell size={24} />
        {unreadCount > 0 && (
            <Badge
                pill
                bg="danger"
                className="position-absolute top-0 start-100 translate-middle header-notification-badge"
            >
                {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
        )}
    </span>
);

const NotificationMenuContent = ({
    notifications,
    unreadCount,
    isLoading,
    isMarkingAsRead,
    onNotificationClick,
    onMarkAllAsRead,
    showTitle = true,
}) => (
    <div className="header-notifications-menu">
        <div className={`header-notifications-head ${showTitle ? '' : 'actions-only'}`}>
            {showTitle && <span className="fw-semibold">Notificaciones</span>}
            <button
                type="button"
                className="btn btn-link btn-sm header-notification-mark-all"
                onClick={onMarkAllAsRead}
                disabled={unreadCount === 0 || isMarkingAsRead}
            >
                <FaCheckDouble className="me-1" />
                Marcar todas
            </button>
        </div>

        <div className="header-notifications-list">
            {isLoading && notifications.length === 0 && (
                <div className="header-notification-empty">Cargando notificaciones...</div>
            )}

            {!isLoading && notifications.length === 0 && (
                <div className="header-notification-empty">No hay notificaciones nuevas</div>
            )}

            {notifications.map((notification) => {
                const id = notification._id || notification.id;
                const isUnread = !notification.isRead;

                return (
                    <button
                        key={id}
                        type="button"
                        className={`header-notification-item ${isUnread ? 'unread' : 'read'}`}
                        onClick={() => onNotificationClick(id)}
                    >
                        <div className="header-notification-title-row">
                            <span className="header-notification-title">{notification.title || 'Notificación'}</span>
                            {isUnread && <span className="header-notification-dot" />}
                        </div>
                        <div className="header-notification-message">{notification.message}</div>
                        <div className="header-notification-date">{formatRelativeDate(notification.createdAt)}</div>
                    </button>
                );
            })}
        </div>
    </div>
);

const HeaderNotificationsDropdown = ({
    notifications,
    unreadCount,
    hideBell,
    isLoading,
    isMarkingAsRead,
    isMobile = false,
    onToggle,
    onNotificationClick,
    onMarkAllAsRead,
}) => {
    const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);

    useBodyScrollLock(isMobile && isMobilePanelOpen);

    if (hideBell) {
        return null;
    }

    if (isMobile) {
        const openMobilePanel = () => {
            setIsMobilePanelOpen(true);
            onToggle?.(true);
        };

        const closeMobilePanel = () => {
            setIsMobilePanelOpen(false);
            onToggle?.(false);
        };

        return (
            <>
                <button
                    type="button"
                    className="header-notifications-trigger"
                    onClick={openMobilePanel}
                    aria-label="Abrir notificaciones"
                >
                    <NotificationBellIcon unreadCount={unreadCount} />
                </button>

                <Offcanvas
                    show={isMobilePanelOpen}
                    onHide={closeMobilePanel}
                    placement="end"
                    className="header-notifications-offcanvas"
                    scroll
                    restoreFocus={false}
                >
                    <Offcanvas.Header closeButton className="header-notifications-offcanvas-header">
                        <Offcanvas.Title>Notificaciones</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body className="header-notifications-offcanvas-body">
                        <NotificationMenuContent
                            notifications={notifications}
                            unreadCount={unreadCount}
                            isLoading={isLoading}
                            isMarkingAsRead={isMarkingAsRead}
                            onNotificationClick={onNotificationClick}
                            onMarkAllAsRead={onMarkAllAsRead}
                            showTitle={false}
                        />
                    </Offcanvas.Body>
                </Offcanvas>
            </>
        );
    }

    return (
        <NavDropdown
            id="admin-notifications-dropdown"
            align="end"
            className="header-nav-dropdown header-notifications-dropdown"
            title={<NotificationBellIcon unreadCount={unreadCount} />}
            onToggle={onToggle}
        >
            <NotificationMenuContent
                notifications={notifications}
                unreadCount={unreadCount}
                isLoading={isLoading}
                isMarkingAsRead={isMarkingAsRead}
                onNotificationClick={onNotificationClick}
                onMarkAllAsRead={onMarkAllAsRead}
            />
        </NavDropdown>
    );
};

export default HeaderNotificationsDropdown;

